/**
 * React Query hooks for PIM Session API (COMP-005).
 * IP-005: technician self-service + admin session management.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@/providers/api-provider";
import type {
  PIMSession,
  PIMActivationRequest,
  PIMExtensionRequest,
  PaginatedResponse,
} from "@/types";

interface SessionListParams {
  page?: number;
  pageSize?: number;
  status?: string;
  customerTenantId?: string;
}

interface SessionStats {
  activeSessions: number;
  totalToday: number;
  averageDuration: number;
}

export function usePimSessions(params: SessionListParams = {}) {
  const api = useApiClient();
  const { page = 1, pageSize = 25, status, customerTenantId } = params;

  const queryParams = new URLSearchParams();
  queryParams.set("page", String(page));
  queryParams.set("pageSize", String(pageSize));
  if (status) queryParams.set("status", status);
  if (customerTenantId) queryParams.set("customerTenantId", customerTenantId);

  return useQuery({
    queryKey: ["pim", "sessions", { page, pageSize, status, customerTenantId }],
    queryFn: () =>
      api.get<PaginatedResponse<PIMSession>>(
        `/api/admin/sessions?${queryParams.toString()}`,
      ),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function usePimSessionsMine(params: { status?: string } = {}) {
  const api = useApiClient();
  const queryParams = params.status ? `?status=${params.status}` : "";

  return useQuery({
    queryKey: ["pim", "sessions", "me", { status: params.status }],
    queryFn: () =>
      api.get<PaginatedResponse<PIMSession>>(`/api/sessions/me${queryParams}`),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function usePimSessionDetail(sessionId: string | undefined) {
  const api = useApiClient();

  return useQuery({
    queryKey: ["pim", "sessions", sessionId],
    queryFn: () => api.get<PIMSession>(`/api/sessions/${sessionId}`),
    enabled: !!sessionId,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function usePimSessionStats() {
  const api = useApiClient();

  return useQuery({
    queryKey: ["pim", "sessions", "stats"],
    queryFn: () => api.get<SessionStats>("/api/admin/sessions/stats"),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useActivateSession() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: PIMActivationRequest) =>
      api.post<PIMSession>("/api/sessions", request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["pim"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard", "stats"] });
    },
  });
}

export function useExtendSession() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      request,
    }: {
      sessionId: string;
      request: PIMExtensionRequest;
    }) => api.put<PIMSession>(`/api/sessions/${sessionId}/extend`, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["pim"] });
    },
  });
}

export function useDeactivateSession() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) =>
      api.del<void>(`/api/sessions/${sessionId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["pim"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard", "stats"] });
    },
  });
}

export function useRevokeSession() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      reason,
    }: {
      sessionId: string;
      reason: string;
    }) =>
      api.del<void>(`/api/admin/sessions/${sessionId}`, {
        body: { reason },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["pim"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard", "stats"] });
    },
  });
}
